import ArtDirectedGatsbyImage from '@root/gatsby-contentful-core/src/components/art-directed-gatsby-image';
import BackgroundColors from '@root/gatsby-contentful-core/src/utils/background-colors';
import PropTypes from '@root/vendor/prop-types';
import Responsive from '@root/core/src/utils/responsive';
import ToutContent from '@root/gatsby-contentful-core/src/components/sections/image-tout-side-by-side/tout-content';
import { IMAGE_POSITION_X, TOUT_LAYOUT_X } from '@root/gatsby-contentful-core/src/components/sections/image-tout-side-by-side/tout-options';
import { getImage } from 'gatsby-plugin-image';
import { styled } from '@root/core/src/utils/styles';

export default function ImageToutSideBySideFullBleed({
  anchor,
  toutContent,
  backgroundColor = BackgroundColors.cmsValues['WHITE'],
  toutLayoutX = TOUT_LAYOUT_X['Image left'],
  image: {
    desktopImg,
    imageAltText,
    mobileImg,
    imagePositionX = IMAGE_POSITION_X['Align center'],
  },
  sectionSpacing = {},
}) {
  return (
    <Section
      backgroundColor={backgroundColor}
      id={anchor}
    >
      <Wrap toutLayoutX={toutLayoutX}>
        <Image
          alt={imageAltText}
          desktopImg={getImage(desktopImg)}
          imgStyle={setImageStyle(imagePositionX)}
          mobileImg={getImage(mobileImg)}
          responsiveBreakpoint={Responsive.QUERIES.lessThanLg}
          toutLayoutX={toutLayoutX}
        />
        <ContentContainer
          sectionSpacing={sectionSpacing}
          toutLayoutX={toutLayoutX}
        >
          <ToutContent {...toutContent} />
        </ContentContainer>
      </Wrap>
    </Section>
  );
}

ImageToutSideBySideFullBleed.propTypes = {
  anchor: PropTypes.string,
  backgroundColor: PropTypes.oneOf(BackgroundColors.cmsValueList),
  image: PropTypes.shape({
    imageAltText: PropTypes.string.isRequired,
    desktopImg: PropTypes.object.isRequired,
    mobileImg: PropTypes.object,
    imagePositionX: PropTypes.oneOf(Object.values(IMAGE_POSITION_X)),
  }).isRequired,
  sectionSpacing: PropTypes.shape({
    bottom: PropTypes.number,
    top: PropTypes.number,
  }),
  toutContent: PropTypes.object.isRequired,
  toutLayoutX: PropTypes.oneOf(Object.values(TOUT_LAYOUT_X)),
};

const Section = styled.section({
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  overflow: 'hidden',
}, ({ backgroundColor }) => BackgroundColors.getBackgroundStyle(backgroundColor));

const Wrap = styled.div({
  ...Responsive.lessThanLg({
    flexDirection: 'column',
  }),
  display: 'flex',
  width: 'min(1200px, 100%)',
  margin: '0 8%',
}, ({ toutLayoutX }) => toutLayoutX === 'IMAGE_RIGHT'
  ? { flexDirection: 'row-reverse' }
  : {});

const Image = styled(ArtDirectedGatsbyImage)({
  ...Responsive.lessThanLg({
    margin: '0 calc(50% - 50vw)',
  }),
  flex: 1,
}, ({ toutLayoutX }) => toutLayoutX === 'IMAGE_RIGHT'
  ? { marginRight: 'calc(50% - 50vw)' }
  : { marginLeft: 'calc(50% - 50vw)' });

const ContentContainer = styled.div({
  ...Responsive.lessThanLg({
    width: '100%',
    padding: '30px 0',
    alignItems: 'center',
    textAlign: 'center',
  }),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  width: '42%',
  padding: '30px 0',
  textAlign: 'left',
  '& > * + *': { marginTop: 14 },
  '& > a': {
    maxWidth: 240,
    marginTop: 35,
  },
}, ({ toutLayoutX, sectionSpacing }) => {
  const toutLayoutXStyles = {
    IMAGE_LEFT: { paddingLeft: '12%' },
    IMAGE_RIGHT: { paddingRight: '12%' },
  };

  const sectionSpacingStyles = {
    ...Responsive.lessThanLg({
      paddingBottom: sectionSpacing.bottom / 1.25,
    }),
    paddingTop: sectionSpacing.top,
    paddingBottom: sectionSpacing.bottom,
  };

  return {
    ...toutLayoutXStyles[toutLayoutX],
    ...sectionSpacingStyles,
  };
});

const setImageStyle = (imagePosition) => {
  const imagePositionXStyles = {
    ALIGN_CENTER: { objectPosition: 'center' },
    ALIGN_LEFT: { objectPosition: 'left' },
    ALIGN_RIGHT: { objectPosition: 'right' },
  };

  return imagePositionXStyles[imagePosition];
};
